import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchData from '../../api/fetchData';
import { useTranslation } from 'react-i18next';
import AdminHeaderManager from './header/AdminHeaderManager';
import AdminPortfolioManager from './portfolio/AdminPortfolioManager';
import AdminMenuManager from './menu/AdminMenuManager';
import AdminHeroEditor from './hero/AdminHeroEditor';
import AdminAboutEditor from './about/AdminAboutEditor';
import AdminServicesManager from './services/AdminServicesManager';
import AdminReviewsManager from './reviews/AdminReviewsManager';
import AdminContactsEditor from './contacts/AdminContactsEditor';
import AdminChatManager from './chat/AdminChatManager';
import AdminSpecialOfferEditor from './special_offer/AdminSpecialOfferEditor';
import AdminCouponOrdersManager from './special_offer/AdminCouponOrdersManager';
import './AdminPanel.css';


function AdminPanel({ onContentUpdated, onLogout }) {
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setLoading(true);
        const currentAdminLang = i18n.language.split('-')[0] || 'ru';

        const response = await fetchData(`/admin/get_admin_data.php?lang=${currentAdminLang}`, 'GET', null, { credentials: 'include' });
        if (response && response.status === 'success') {
          setAdminData(response);

        } else {
          const errorMessage = response?.message || 'Не удалось загрузить данные административной панели.';
          setError(errorMessage);

          if (response?.message === 'Неавторизованный доступ') {
            navigate('/admin/login');
          }
        }
      } catch (err) {
        setError('Произошла ошибка при получении данных: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [navigate, i18n.language]);



  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    navigate('/');
  };

  const adminSections = [
    { id: 'header', titleKey: 'admin.header.managerTitle' },
    { id: 'menu', titleKey: 'admin.menu.managerTitle' },
    { id: 'hero', titleKey: 'admin.hero.managerTitle' },
    { id: 'about', titleKey: 'admin.about.managerTitle' },
    { id: 'portfolio', titleKey: 'admin.portfolio.managerTitle' },
    { id: 'services', titleKey: 'admin.services.managerTitle' },
    { id: 'special-offer', titleKey: 'admin.specialOffer.managerTitle' },
    { id: 'coupon-orders', titleKey: 'admin.couponOrders.managerTitle' },
    { id: 'reviews', titleKey: 'admin.reviews.managerTitle' },
    { id: 'contacts', titleKey: 'admin.contacts.managerTitle' },
    { id: 'chat', titleKey: 'admin.chat.managerTitle' }
  ];

  if (loading) return <div>{t('messages.loading')}</div>;
  if (error) return <div className="error-message">{t('messages.error.generic', { details: error })}</div>;
  if (!adminData) return <div>{t('admin.noData', 'Нет данных для отображения.')}</div>;

  return (
    <div className="admin-panel">
      <h2>{t('admin.panelTitle', 'Административная панель')}</h2>
      <p>{t('admin.welcomeMessage', { name: adminData?.admin_info?.username || 'администратор' })}</p>
      <button onClick={onLogout}>{t('header.logoutButton')}</button>

      {/* --- 2. ДОБАВЛЯЕМ САМО НАВИГАЦИОННОЕ МЕНЮ --- */}
      <nav className="admin-nav">
        {adminSections.map(section => (
          <a key={section.id} href={`#admin-${section.id}`}>{t(section.titleKey)}</a>
        ))}
      </nav>

      {/* --- 3. ОБОРАЧИВАЕМ КАЖДЫЙ КОМПОНЕНТ В DIV С ID --- */}
      <div id="admin-header" className="admin-section-wrapper">
        <AdminHeaderManager onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-menu" className="admin-section-wrapper">
        <AdminMenuManager onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-hero" className="admin-section-wrapper">
        <AdminHeroEditor onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-about" className="admin-section-wrapper">
        <AdminAboutEditor onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-portfolio" className="admin-section-wrapper">
        <AdminPortfolioManager onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-services" className="admin-section-wrapper">
        <AdminServicesManager onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-special-offer" className="admin-section-wrapper">
        <AdminSpecialOfferEditor onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-coupon-orders" className="admin-section-wrapper">
        <AdminCouponOrdersManager />
      </div>
      <hr />
      <div id="admin-reviews" className="admin-section-wrapper">
        <AdminReviewsManager onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-contacts" className="admin-section-wrapper">
        <AdminContactsEditor onContentUpdated={onContentUpdated} />
      </div>
      <hr />
      <div id="admin-chat" className="admin-section-wrapper">
        <AdminChatManager />
      </div>
      <hr />

      <h3>{t('admin.statsTitle', 'Статистика')}:</h3>
      <p>{t('admin.usersCount', 'Всего пользователей')}: {adminData.usersCount ?? 'N/A'}</p>
      <p>{t('admin.pendingReviewsCount', 'Ожидают модерации отзывы')}: {adminData.pendingReviewsCount ?? 'N/A'}</p>

    </div>
  );
}

export default AdminPanel;